import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PlainDate } from 'temporal-luxon';
import { LibraryManager, ResourceType } from 'libs.library_manager';
import { Button, DatePicker, FormSections, Icon, Input, ModalWindow, Select, Spinner, useLocalizeMessage, } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { FieldPicker } from '../../../charts/components/settings_panel/data_section/field_picker.component';
import { toSnakeCase } from '../../../data_views/utils/utils';
import { getCubeApi } from '../../../transfer_templates/hooks/use_cube_api/use_cube_api.hook';
import { getFilterFieldOptionsForRow, hasAvailableFilterFields, isFreeTextOperator, isNumericFieldType, isRangeOperator, OPERATORS_BY_FIELD_TYPE, } from '../../utils';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const FIELD_TYPE_LABELS = {
    string: 'Text',
    number: 'Number',
    time: 'Date',
    boolean: 'Boolean',
};
const getFieldTypeLabel = (cubeType) => FIELD_TYPE_LABELS[cubeType] ?? cubeType;
let nextRowId = 0;
const generateRowId = () => `row-${++nextRowId}`;
const filtersToRows = (filters, dimensionTypeMap) => filters.map((f) => {
    const cubeType = dimensionTypeMap.get(f.key) ?? 'string';
    const operators = OPERATORS_BY_FIELD_TYPE[cubeType] || OPERATORS_BY_FIELD_TYPE.string;
    const effectiveOperator = f.defaultOperator || f.operator || operators[0]?.value || 'equals';
    return {
        rowId: generateRowId(),
        key: f.key,
        label: f.label,
        fieldType: getFieldTypeLabel(cubeType),
        cubeType,
        operator: effectiveOperator,
        isDefaultOpen: !!f.defaultValues?.length,
        defaultValues: f.defaultValues,
    };
});
const rowsToFilters = (rows) => rows
    .filter((r) => r.key)
    .map((r) => {
    const filter = {
        label: r.label,
        key: r.key,
        operator: r.operator || 'equals',
        query: { dimensions: [r.key] },
    };
    if (r.cubeType) {
        filter.fieldType = r.cubeType;
    }
    if (r.cubeType === 'number' || r.defaultValues?.length) {
        filter.defaultOperator = r.operator || 'equals';
    }
    if (r.defaultValues?.length) {
        filter.defaultValues = r.defaultValues;
    }
    return filter;
});
const DefaultValueEditor = ({ row, studyId, environmentId, cubeConfigIds, onValuesChange, onRemove, onLoadingChange, }) => {
    const translate = useLocalizeMessage();
    const { entityId } = useContext(AuthContext);
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const cubeConfigIdsKey = cubeConfigIds.join(',');
    const prevFetchKeyRef = useRef('');
    const isTimeField = row.cubeType === 'time';
    const isNumericField = isNumericFieldType(row.cubeType);
    // `contains` (and other LIKE-style operators) take user-supplied substrings,
    // not values from the dataset, so we render a free-text input instead of the
    // value picker and skip the Cube.js fetch.
    const isFreeText = isFreeTextOperator(row.operator);
    useEffect(() => {
        if (isTimeField || isFreeText || isNumericField) {
            setIsLoading(false);
            return;
        }
        if (!studyId || !environmentId || !entityId || !row.key) {
            setIsLoading(false);
            return;
        }
        const fetchKey = `${row.key}|${studyId}|${environmentId}|${entityId}|${cubeConfigIdsKey}`;
        if (fetchKey === prevFetchKeyRef.current) {
            return;
        }
        prevFetchKeyRef.current = fetchKey;
        let cancelled = false;
        const cubeApi = getCubeApi({
            entityId: entityId.toString(),
            studyId,
            activeReportsTab: 0,
            selectedTestEnv: { label: '', value: environmentId },
            prodOrgs: [],
            env: 'test',
            cubeConfigIds,
        });
        const loadOptions = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const query = { dimensions: [row.key], renewQuery: true };
                const result = await cubeApi.load(query, { cache: 'must-revalidate' });
                if (cancelled) {
                    return;
                }
                const pivotData = result.tablePivot();
                const loadedOptions = pivotData
                    .map((r) => {
                    const value = r[row.key];
                    if (value === null || value === undefined || value === '') {
                        return null;
                    }
                    return { label: String(value), value: String(value) };
                })
                    .filter(Boolean);
                // Ensure default values always appear even if not in the Cube response
                if (row.defaultValues?.length) {
                    const loadedValueSet = new Set(loadedOptions.map((o) => o.value));
                    const missingDefaults = row.defaultValues
                        .filter((v) => !loadedValueSet.has(v))
                        .map((v) => ({ label: v, value: v }));
                    setOptions([...missingDefaults, ...loadedOptions]);
                }
                else {
                    setOptions(loadedOptions);
                }
            }
            catch {
                if (!cancelled) {
                    setError('Failed to load filter options');
                }
            }
            finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };
        loadOptions();
        return () => {
            cancelled = true;
        };
    }, [row.key, studyId, environmentId, entityId, cubeConfigIdsKey, isTimeField, isFreeText, isNumericField]);
    // Mirror the local loading flag up to the modal so it can disable the Add field
    // button while a fetch is in flight, and clear our entry when this row unmounts.
    useEffect(() => {
        onLoadingChange?.(row.rowId, isLoading);
    }, [isLoading, row.rowId, onLoadingChange]);
    useEffect(() => () => {
        onLoadingChange?.(row.rowId, false);
    }, [row.rowId, onLoadingChange]);
    const selectedValues = useMemo(() => (row.defaultValues || []).map((v) => ({ label: v, value: v })), [row.defaultValues]);
    const freeTextValue = row.defaultValues?.[0] ?? '';
    const numericValue = row.defaultValues?.[0] ?? '';
    const isDateRange = isTimeField && isRangeOperator(row.operator);
    const parseDateValue = (raw) => {
        if (!raw) {
            return undefined;
        }
        try {
            return PlainDate.from(raw.split('T')[0]);
        }
        catch {
            return undefined;
        }
    };
    const singleDateValue = useMemo(() => {
        if (!isTimeField || isDateRange) {
            return undefined;
        }
        return parseDateValue(row.defaultValues?.[0]);
    }, [isTimeField, isDateRange, row.defaultValues]);
    const fromDateValue = useMemo(() => (isDateRange ? parseDateValue(row.defaultValues?.[0]) : undefined), [isDateRange, row.defaultValues]);
    const toDateValue = useMemo(() => (isDateRange ? parseDateValue(row.defaultValues?.[1]) : undefined), [isDateRange, row.defaultValues]);
    const handleDateChange = useCallback((date) => {
        onValuesChange(row.rowId, date ? [date.toString()] : []);
    }, [row.rowId, onValuesChange]);
    const handleRangeDateChange = useCallback((which, date) => {
        const iso = date?.toString() ?? '';
        const currentFrom = row.defaultValues?.[0] ?? '';
        const currentTo = row.defaultValues?.[1] ?? '';
        const nextFrom = which === 'from' ? iso : currentFrom;
        const nextTo = which === 'to' ? iso : currentTo;
        if (!nextFrom && !nextTo) {
            onValuesChange(row.rowId, []);
            return;
        }
        onValuesChange(row.rowId, [nextFrom, nextTo]);
    }, [row.rowId, row.defaultValues, onValuesChange]);
    return (_jsxs("div", { className: 'flex flex-col gap-2 ml-1 pl-3 border-l-2 border-neutral-200', children: [_jsxs("div", { className: 'flex items-center justify-between', children: [_jsx("span", { className: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-secondary', children: translate('Default value:') }), _jsx(Button, { label: translate('Remove default'), variant: 'transparent', content: _jsx(Icon, { name: 'close', size: 'sm', color: 'current' }), onClick: () => onRemove(row.rowId) })] }), _jsx("div", { className: 'relative', children: isTimeField ? (isDateRange ? (_jsxs("div", { className: 'grid grid-cols-2 gap-2', children: [_jsx(DatePicker, { id: `default-date-from-${row.rowId}`, value: fromDateValue, onChange: (date) => handleRangeDateChange('from', date), width: 'full' }), _jsx(DatePicker, { id: `default-date-to-${row.rowId}`, value: toDateValue, onChange: (date) => handleRangeDateChange('to', date), width: 'full' })] })) : (_jsx(DatePicker, { id: `default-date-${row.rowId}`, value: singleDateValue, onChange: handleDateChange, width: 'full' }))) : isFreeText ? (_jsx(Input, { id: `default-text-${row.rowId}`, value: freeTextValue, onChange: (e) => {
                        const next = e.target.value;
                        onValuesChange(row.rowId, next ? [next] : []);
                    }, width: 'full', placeholder: translate('Enter text to match') })) : isNumericField ? (_jsx(Input, { id: `default-numeric-${row.rowId}`, type: 'number', value: numericValue, onChange: (e) => {
                        const next = e.target.value;
                        onValuesChange(row.rowId, next ? [next] : []);
                    }, width: 'full', placeholder: translate('Enter a number') })) : (_jsxs(_Fragment, { children: [isLoading && (_jsx("div", { className: 'absolute z-[5] inset-0 flex items-center justify-center', children: _jsx(Spinner, { size: 'sm' }) })), error ? (_jsx("span", { className: 'nucleus-text-sm text-red-500', children: error })) : (_jsx(Select, { disabled: isLoading, options: options, value: selectedValues, onChange: (vals) => onValuesChange(row.rowId, vals.map((v) => v.value)), width: 'full', multiple: true, searchableOptions: true, placeholder: translate('Select default value') }))] })) })] }));
};
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const EditFiltersModal = ({ isOpen, onClose, onSave, dashboardName, cubeConfigId, existingFilters, studyId = '', environmentId = '', cubeConfigIds = [], studyOptions, environmentOptions, selectedStudy, selectedEnvironment, onStudyChange, onEnvironmentChange, isLoadingStudies, isLoadingEnvironments, }) => {
    const translate = useLocalizeMessage();
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const [rows, setRows] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    // Tracks which rows are currently fetching their default-value options.
    // Used to block "Add field" while any row is loading so we don't kick off
    // a fresh load on top of an in-flight one.
    const [loadingRowIds, setLoadingRowIds] = useState(new Set());
    const handleRowLoadingChange = useCallback((rowId, isLoading) => {
        setLoadingRowIds((prev) => {
            const wasLoading = prev.has(rowId);
            if (wasLoading === isLoading) {
                return prev;
            }
            const next = new Set(prev);
            if (isLoading) {
                next.add(rowId);
            }
            else {
                next.delete(rowId);
            }
            return next;
        });
    }, []);
    const isAnyRowLoading = loadingRowIds.size > 0;
    const canConfigureDefaults = !!(studyId && environmentId);
    // ---------------------------------------------------------------------------
    // Load dataset schema
    // ---------------------------------------------------------------------------
    const fetchDatasetSchema = useCallback(async () => {
        if (!cubeConfigId) {
            return { cubeName: '', dimensions: {} };
        }
        const libraryManager = new LibraryManager({ axiosInstance: libraryClient });
        const resource = await libraryManager.getResourceById(cubeConfigId, ResourceType.CUBE_CONFIG);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = resource?.data || {};
        return {
            cubeName: data.name ? toSnakeCase(data.name) : '',
            dimensions: (data.dimensions || {}),
        };
    }, [cubeConfigId, libraryClient]);
    const { data: datasetSchema, isLoading: isLoadingSchema } = useQuery({
        queryKey: ['dataset-schema-filters', cubeConfigId],
        queryFn: fetchDatasetSchema,
        enabled: !!cubeConfigId && isOpen,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
    });
    const cubeName = datasetSchema?.cubeName || '';
    const dimensionTypeMap = useMemo(() => {
        const map = new Map();
        for (const [key, def] of Object.entries(datasetSchema?.dimensions || {})) {
            if (def.public === false) {
                continue;
            }
            const fqKey = cubeName ? `${cubeName}.${key}` : key;
            map.set(fqKey, def.type);
        }
        return map;
    }, [datasetSchema?.dimensions, cubeName]);
    const allDimensionFields = useMemo(() => Object.entries(datasetSchema?.dimensions || {})
        .filter(([, def]) => def.public !== false)
        .map(([key, def]) => ({
        key: cubeName ? `${cubeName}.${key}` : key,
        name: def.title || key,
        type: getFieldTypeLabel(def.type),
    })), [datasetSchema?.dimensions, cubeName]);
    // ---------------------------------------------------------------------------
    // Initialise rows when modal opens
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (isOpen) {
            setRows(existingFilters.length > 0 ? filtersToRows(existingFilters, dimensionTypeMap) : []);
        }
    }, [isOpen, existingFilters, dimensionTypeMap]);
    // ---------------------------------------------------------------------------
    // Row manipulation
    // ---------------------------------------------------------------------------
    const selectedFilterKeys = useMemo(() => rows.map((row) => row.key), [rows]);
    const canAddFilterField = hasAvailableFilterFields(allDimensionFields, selectedFilterKeys);
    const allFieldsAddedMessage = translate('All fields have been added');
    const getOptionsForRow = useCallback((rowId) => {
        const currentRow = rows.find((row) => row.rowId === rowId);
        return getFilterFieldOptionsForRow(allDimensionFields, selectedFilterKeys, currentRow?.key);
    }, [allDimensionFields, rows, selectedFilterKeys]);
    const handleFieldChange = useCallback((rowId, field) => {
        setRows((prev) => prev.map((r) => {
            if (r.rowId !== rowId) {
                return r;
            }
            const cubeType = dimensionTypeMap.get(field.key) ?? 'string';
            const operators = OPERATORS_BY_FIELD_TYPE[cubeType] || OPERATORS_BY_FIELD_TYPE.string;
            return {
                ...r,
                key: field.key,
                label: field.name,
                fieldType: field.type,
                cubeType,
                operator: operators[0]?.value ?? 'equals',
                isDefaultOpen: false,
                defaultValues: undefined,
            };
        }));
    }, [dimensionTypeMap]);
    const handleAddField = useCallback(() => {
        setRows((prev) => [
            ...prev,
            {
                rowId: generateRowId(),
                key: '',
                label: '',
                fieldType: '',
                cubeType: '',
                operator: 'equals',
                isDefaultOpen: false,
            },
        ]);
    }, []);
    const handleRemoveField = useCallback((rowId) => {
        setRows((prev) => prev.filter((r) => r.rowId !== rowId));
    }, []);
    // ---------------------------------------------------------------------------
    // Default value manipulation
    // ---------------------------------------------------------------------------
    const handleToggleDefault = useCallback((rowId) => {
        setRows((prev) => prev.map((r) => {
            if (r.rowId !== rowId) {
                return r;
            }
            return r.isDefaultOpen
                ? { ...r, isDefaultOpen: false, defaultValues: undefined }
                : { ...r, isDefaultOpen: true };
        }));
    }, []);
    const handleFilterOperatorChange = useCallback((rowId, operator) => {
        setRows((prev) => prev.map((r) => {
            if (r.rowId !== rowId) {
                return r;
            }
            if (isNumericFieldType(r.cubeType)) {
                return { ...r, operator };
            }
            return { ...r, operator, defaultValues: undefined };
        }));
    }, []);
    const handleDefaultValuesChange = useCallback((rowId, values) => {
        setRows((prev) => prev.map((r) => (r.rowId === rowId ? { ...r, defaultValues: values } : r)));
    }, []);
    const handleRemoveDefault = useCallback((rowId) => {
        setRows((prev) => prev.map((r) => (r.rowId === rowId ? { ...r, isDefaultOpen: false, defaultValues: undefined } : r)));
    }, []);
    // ---------------------------------------------------------------------------
    // Save / Close
    // ---------------------------------------------------------------------------
    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            await onSave(rowsToFilters(rows));
            onClose();
        }
        finally {
            setIsSaving(false);
        }
    }, [rows, onSave, onClose]);
    const handleClose = useCallback(() => {
        setRows([]);
        onClose();
    }, [onClose]);
    // ---------------------------------------------------------------------------
    // Footer buttons (following create_dashboard_modal pattern)
    // ---------------------------------------------------------------------------
    const saveButton = {
        label: translate('Save'),
        onClick: handleSave,
        disabled: isSaving,
    };
    const footerSecondaryActionButtons = [
        {
            label: translate('Cancel'),
            onClick: handleClose,
            disabled: isSaving,
        },
    ];
    // ---------------------------------------------------------------------------
    // Form section content
    // ---------------------------------------------------------------------------
    const showStudyEnvSection = !!(studyOptions && environmentOptions && onStudyChange && onEnvironmentChange);
    const formSections = [
        ...(showStudyEnvSection
            ? [
                {
                    title: translate('Study and environment'),
                    description: translate('Filter values are loaded from the selected study and environment.'),
                    content: (_jsxs("div", { className: 'flex items-end gap-4 flex-wrap', children: [_jsx("div", { className: 'w-40', children: _jsx(Select, { label: translate('Study'), placeholder: translate('Select study'), options: studyOptions ?? [], value: selectedStudy, onChange: (opt) => onStudyChange?.(opt), width: 'full', disabled: !!isLoadingStudies, searchableOptions: true }) }), _jsx("div", { className: 'w-48', children: _jsx(Select, { label: translate('Environment'), placeholder: translate('Select environment'), options: environmentOptions ?? [], value: selectedEnvironment, onChange: (opt) => onEnvironmentChange?.(opt), width: 'full', disabled: !studyId || !!isLoadingEnvironments, searchableOptions: true }) })] })),
                },
            ]
            : []),
        {
            title: translate('Global filter fields'),
            description: translate('Select which fields will be available as global filters.'),
            content: isLoadingSchema ? (_jsx("div", { className: 'flex items-center justify-center py-4', children: _jsx(Spinner, { size: 'sm' }) })) : (_jsxs("div", { className: 'flex flex-col gap-3 max-w-input-lg', children: [rows.map((row) => {
                        const selectedValue = row.key
                            ? (allDimensionFields.find((f) => f.key === row.key) ?? undefined)
                            : undefined;
                        const cubeType = row.cubeType || 'string';
                        const operators = OPERATORS_BY_FIELD_TYPE[cubeType] || OPERATORS_BY_FIELD_TYPE.string;
                        const operatorOptions = operators.map((o) => ({
                            label: translate(o.labelKey),
                            value: o.value,
                        }));
                        const selectedOperator = operatorOptions.find((o) => o.value === row.operator) ?? operatorOptions[0];
                        return (_jsxs("div", { className: 'flex flex-col gap-2 rounded-lg border border-neutral-200 p-3', children: [_jsxs("div", { className: 'flex items-center gap-1 min-w-0', children: [_jsx("div", { className: 'flex-1 min-w-0', children: _jsx(FieldPicker, { value: selectedValue, options: getOptionsForRow(row.rowId), onChange: (field) => handleFieldChange(row.rowId, field), placeholder: translate('Select a field'), emptyOptionsMessage: allFieldsAddedMessage }) }), _jsx(Button, { label: translate('Remove field'), variant: 'transparent', content: _jsx(Icon, { name: 'delete', size: 'sm', color: 'current' }), onClick: () => handleRemoveField(row.rowId) })] }), row.key && (_jsxs("div", { className: 'w-48', children: [_jsx("span", { className: 'nucleus-font-medium nucleus-text-xs nucleus-text-text-secondary nucleus-block nucleus-mb-1', children: translate('Operator') }), _jsx(Select, { options: operatorOptions, value: selectedOperator, onChange: (opt) => handleFilterOperatorChange(row.rowId, opt.value), width: 'full' })] })), row.isDefaultOpen && row.key ? (canConfigureDefaults ? (_jsx(DefaultValueEditor, { row: row, studyId: studyId, environmentId: environmentId, cubeConfigIds: cubeConfigIds, onValuesChange: handleDefaultValuesChange, onRemove: handleRemoveDefault, onLoadingChange: handleRowLoadingChange })) : (_jsxs("div", { className: 'flex items-center gap-2 ml-1 pl-3 border-l-2 border-neutral-200 py-1', children: [_jsx("span", { className: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-secondary', children: translate('Default:') }), _jsx("span", { className: 'nucleus-text-sm nucleus-text-text-primary', children: (row.defaultValues || []).join(', ') })] }))) : (row.key && (_jsx("div", { className: 'self-start', children: _jsx(Button, { label: `+ ${translate('Add default value')}`, variant: 'transparent', onClick: () => handleToggleDefault(row.rowId), disabled: !canConfigureDefaults, dataTestId: `add-default-value-${row.rowId}` }) })))] }, row.rowId));
                    }), canAddFilterField && (_jsx("div", { className: 'self-start', children: _jsx(Button, { label: `+ ${translate('Add field')}`, variant: 'transparent', onClick: handleAddField, 
                            // Block Add field until the dataset schema is loaded, while any row is still
                            // fetching options, and until a study and environment have been selected
                            // (without those, no cube query can run, so there's no data yet to filter on).
                            disabled: allDimensionFields.length === 0 || isAnyRowLoading || !canConfigureDefaults, dataTestId: 'add-filter-field-button' }) }))] })),
        },
    ];
    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    return (_jsx(ModalWindow, { isOpen: isOpen, closeWindow: handleClose, title: translate('Edit filters'), subtitle: dashboardName, width: 'full', footerPrimaryActionButton: saveButton, footerSecondaryActionButtons: footerSecondaryActionButtons, dataTestId: 'edit-filters-modal', children: _jsx("div", { className: 'min-h-[400px] size-full', children: _jsx(FormSections, { sections: formSections }) }) }));
};
